
import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Share2, Copy, Film, Image as ImageIcon, File } from "lucide-react";
import { Image } from "@/lib/types";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";

interface ImageCardProps {
  image: Image;
  onDelete: (id: string) => void;
}

const ImageCard = ({ image, onDelete }: ImageCardProps) => {
  const [copied, setCopied] = useState(false);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [mediaError, setMediaError] = useState(false);

  // Always use absolute URLs with origin to ensure they work in production
  const shareUrl = `${window.location.origin}/img/${image.slug}`;
  
  // Determine media type based on content type or URL
  const getMediaType = (): 'video' | 'gif' | 'image' | 'document' => {
    if (image.contentType) {
      if (image.contentType.startsWith('video/')) return 'video';
      if (image.contentType === 'image/gif') return 'gif';
      if (image.contentType.startsWith('image/')) return 'image';
      return 'document';
    }
    
    // Fallback to URL extension checking
    const url = image.url.toLowerCase();
    if (url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.mov')) return 'video';
    if (url.endsWith('.gif')) return 'gif';
    if (url.endsWith('.jpg') || url.endsWith('.jpeg') || url.endsWith('.png') || url.endsWith('.webp')) return 'image';
    
    return 'document';
  };
  
  const mediaType = getMediaType();
  const isVideo = mediaType === 'video';
  const isGif = mediaType === 'gif';

  const handleCopyLink = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied to clipboard");
    
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // If Web Share API is available, use it
    if (navigator.share) {
      try {
        await navigator.share({
          title: image.name,
          text: image.description || `Check out this ${mediaType}: ${image.name}`,
          url: shareUrl,
        });
        toast.success("Shared successfully");
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error("Error sharing:", error);
          // Fall back to copy if sharing failed for reasons other than user cancellation
          handleCopyLink(e);
        }
      }
    } else {
      // Fall back to copy if Web Share API is not available
      handleCopyLink(e);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (confirm("Are you sure you want to delete this item?")) {
      onDelete(image.id);
    }
  };

  const handleMediaLoad = () => {
    setMediaLoaded(true);
  };

  const handleMediaError = () => {
    setMediaError(true);
    console.error("Failed to load media:", image.url);
  };

  const renderMedia = () => {
    if (mediaType === 'document') {
      // For documents, show a file preview instead of trying to load as image
      const fileExtension = image.name?.split('.').pop()?.toUpperCase() || 'FILE';
      return (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/20 text-white/90">
          <File className="h-12 w-12 mb-2 text-primary/70" />
          <p className="text-sm font-medium text-center px-2">{image.name}</p>
          <p className="text-xs text-white/60 mt-1">{fileExtension}</p>
        </div>
      );
    }
    
    if (mediaError) {
      return (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/20 text-white/60">
          <p>Failed to load {mediaType}</p>
        </div>
      );
    }
    
    if (isVideo) {
      return (
        <>
          <video
            src={image.url}
            className={`w-full h-full object-cover ${!mediaLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
            onLoadedData={handleMediaLoad}
            onError={handleMediaError}
            muted
            loop
            playsInline
            autoPlay={false}
            crossOrigin="anonymous"
          />
          <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1">
            <Film size={12} />
            <span>Video</span>
          </div>
        </>
      );
    } else {
      return (
        <>
          <img
            src={image.url}
            alt={image.name}
            className={`w-full h-full object-cover ${!mediaLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
            onLoad={handleMediaLoad}
            onError={handleMediaError}
            crossOrigin="anonymous"
          />
          {isGif && (
            <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-md">
              GIF
            </div>
          )}
        </>
      );
    }
  };

  return (
    <Link to={shareUrl}>
      <Card className="glass-card overflow-hidden h-full transition-transform duration-200 hover:scale-[1.02] hover:shadow-lg">
        <div className="aspect-video bg-black/30 overflow-hidden relative">
          {!mediaLoaded && !mediaError && mediaType !== 'document' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Skeleton className="h-full w-full absolute" />
            </div>
          )}
          
          {renderMedia()}
        </div>
        <CardContent className="p-3 flex items-center justify-between">
          <div className="truncate mr-2">
            <h3 className="font-medium truncate">{image.name}</h3>
            <p className="text-sm text-white/60 truncate">
              {image.views} {image.views === 1 ? 'view' : 'views'}
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 flex items-center justify-center rounded-full hover:bg-black/20"
              onClick={handleShare}
              title="Share"
            >
              <Share2 size={16} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 flex items-center justify-center rounded-full hover:bg-black/20"
              onClick={handleCopyLink}
              title="Copy link"
            >
              <Copy size={16} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 flex items-center justify-center rounded-full text-destructive hover:bg-destructive/10"
              onClick={handleDelete}
              title="Delete"
            >
              ×
            </Button>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default ImageCard;
